import { useState } from 'react'
import { CurrencyField } from '@/components/currency-field'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { PlusIcon, Trash2Icon } from 'lucide-react'
import {
	Button,
	Form, FormControl, FormField, FormItem, FormLabel,
	IconButton,
	Input,
	Label,
	Checkbox,
	RadioGroup, RadioOption,
	SectionHeading,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
	Switch,
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@rentflowdev/ui'
import { DEFAULT_LOCALE } from '@/types'
import { EditableSectionHeader } from './editable-section-header'
import {
	PAYMENT_PERIOD_STARTS,
	type PaymentPeriodStart,
	type PaymentsUtilityRow,
	type PaymentsValues,
} from './types'

const UTILITY_CALC_KEYS = ['fixed', 'byMeters'] as const

/**
 * Local edit model for a utility row — `amount` is kept as the raw input
 * string while editing so decimal separators survive typing ('1500.5' must
 * not collapse to '15005' via an eager parse → String() re-render cycle).
 * Parsed to a number only on blur, propagation and submit.
 */
interface UtilityRowDraft {
	id:          string
	name:        string
	calcType:    string
	amountInput: string
}

function toDraft(row: PaymentsUtilityRow): UtilityRowDraft {
	return {
		id:          row.id,
		name:        row.name,
		calcType:    row.calcType,
		amountInput: row.amount === null ? '' : String(row.amount),
	}
}

function toRow(draft: UtilityRowDraft): PaymentsUtilityRow {
	return {
		id:       draft.id,
		name:     draft.name,
		calcType: draft.calcType,
		amount:   draft.amountInput.trim() === '' ? null : parseNumberInput(draft.amountInput),
	}
}

function makeEmptyUtilityDraft(): UtilityRowDraft {
	return { id: crypto.randomUUID(), name: '', calcType: 'fixed', amountInput: '0' }
}

const cellInputClass =
	'w-full rounded-(--radius-sm) bg-transparent px-2 py-1 text-sm leading-5 text-(--color-foreground) placeholder:text-(--color-muted-foreground) focus:bg-(--color-muted)/40 focus:outline-none read-only:text-(--color-muted-foreground) read-only:cursor-default'

const paymentsSchema = z.object({
	rentAmount:       z.number().min(0),
	rentCurrency:     z.string(),
	rentVatRate:      z.number().min(0).max(100),
	rentVatClause:    z.string().optional(),
	rentVatInclusive: z.boolean(),

	depositEnabled:         z.boolean(),
	depositAmount:          z.number().min(0),
	depositCurrency:        z.string(),
	depositTermCount:       z.number().min(0),
	depositTermUnit:        z.string(),
	depositTermTiming:      z.string(),
	depositReturn:          z.string(),
	depositNoReturnOnEarly: z.boolean(),

	penaltyEnabled:    z.boolean(),
	penaltyPercent:    z.number().min(0),
	penaltyGraceCount: z.number().min(0),
	penaltyGraceUnit:  z.string(),

	firstPaymentDays:  z.number().min(0),
	paymentForm:       z.string(),
	landlordAccountId: z.string(),

	periodStart:     z.enum(PAYMENT_PERIOD_STARTS as [PaymentPeriodStart, ...PaymentPeriodStart[]]),
	paymentDay:      z.string(),
	paymentPriority: z.string(),
})

type PaymentsFormSchema = z.infer<typeof paymentsSchema>

const UNIT_KEYS           = ['months', 'days'] as const
const TIMING_KEYS         = ['afterContractEnd', 'onAgreedDate'] as const
const DEPOSIT_RETURN_KEYS = ['lastPeriodOffset', 'refund'] as const
const PAYMENT_FORM_KEYS   = ['cashless', 'cash'] as const
const PAYMENT_DAY_KEYS    = ['every5th', 'every10th', 'every15th', 'every20th'] as const
const PRIORITY_KEYS       = ['penaltyOverdueCurrent', 'currentOverduePenalty'] as const

function formatCurrency(n: number): string {
	if (!Number.isFinite(n)) return '—'
	return n.toLocaleString(DEFAULT_LOCALE)
}

function parseNumberInput(raw: string): number {
	const n = parseFloat(raw.replace(',', '.'))
	return Number.isFinite(n) ? n : 0
}

function parseIntSafe(raw: string): number {
	const n = parseInt(raw, 10)
	return Number.isFinite(n) ? n : 0
}

// Stub — real schedule will be derived server-side once backend #63 ships.
const SCHEDULE_PREVIEW_ROWS = [
	{ idx: 1, period: '17.02.2026 – 28.02.2026', dueDate: '25.03.2026' },
	{ idx: 2, period: '17.02.2026 – 28.02.2026', dueDate: '25.03.2026' },
	{ idx: 3, period: '17.02.2026 – 28.02.2026', dueDate: '25.03.2026' },
]

interface StepPaymentsProps {
	initial: PaymentsValues
	formId:  string
	onValid: (values: PaymentsValues) => void
	/**
	 * Fires on every utilities edit so the wizard keeps `state.payments.utilities`
	 * in sync — `onValid` only fires on the final save, so without this Back /
	 * stepper navigation and draft persistence would silently lose the edits.
	 */
	onUtilitiesChange?: (utilities: PaymentsUtilityRow[]) => void
}

export function StepPayments({ initial, formId, onValid, onUtilitiesChange }: StepPaymentsProps) {
	const { t } = useTranslation()

	// Utilities live outside RHF (list-shaped, similar to `step-subject` services).
	const [utilities, setUtilities] = useState<UtilityRowDraft[]>(() => initial.utilities.map(toDraft))
	const [utilitiesEditable, setUtilitiesEditable] = useState(false)
	const [utilitiesError, setUtilitiesError] = useState<string | undefined>(undefined)

	function commitUtilities(next: UtilityRowDraft[]) {
		setUtilities(next)
		setUtilitiesError(undefined)
		onUtilitiesChange?.(next.map(toRow))
	}

	function updateUtilityRow<K extends keyof UtilityRowDraft>(id: string, key: K, value: UtilityRowDraft[K]) {
		commitUtilities(utilities.map((r) => (r.id === id ? { ...r, [key]: value } : r)))
	}

	function addUtilityRow() {
		commitUtilities([...utilities, makeEmptyUtilityDraft()])
	}

	function removeUtilityRow(id: string) {
		commitUtilities(utilities.filter((r) => r.id !== id))
	}

	const form = useForm<PaymentsFormSchema>({
		resolver: zodResolver(paymentsSchema),
		defaultValues: {
			rentAmount:       initial.rentAmount,
			rentCurrency:     initial.rentCurrency,
			rentVatRate:      initial.rentVatRate,
			rentVatClause:    initial.rentVatClause,
			rentVatInclusive: initial.rentVatInclusive,

			depositEnabled:         initial.depositEnabled,
			depositAmount:          initial.depositAmount,
			depositCurrency:        initial.depositCurrency,
			depositTermCount:       initial.depositTermCount,
			depositTermUnit:        initial.depositTermUnit,
			depositTermTiming:      initial.depositTermTiming,
			depositReturn:          initial.depositReturn,
			depositNoReturnOnEarly: initial.depositNoReturnOnEarly,

			penaltyEnabled:    initial.penaltyEnabled,
			penaltyPercent:    initial.penaltyPercent,
			penaltyGraceCount: initial.penaltyGraceCount,
			penaltyGraceUnit:  initial.penaltyGraceUnit,

			firstPaymentDays:  initial.firstPaymentDays,
			paymentForm:       initial.paymentForm,
			landlordAccountId: initial.landlordAccountId,

			periodStart:     initial.periodStart,
			paymentDay:      initial.paymentDay,
			paymentPriority: initial.paymentPriority,
		},
		mode: 'onChange',
	})

	function handleSubmit(values: PaymentsFormSchema) {
		const rows = utilities.map(toRow)
		// Drop rows that are entirely blank (no name, zero/empty amount) so
		// placeholder rows never leak into the payload.
		const kept = rows.filter((r) => r.name.trim() !== '' || (r.amount !== null && r.amount !== 0))
		// A row with an amount but no name is ambiguous — block the submit.
		if (kept.some((r) => r.name.trim() === '')) {
			setUtilitiesError(t('deals.documents.wizard.payments.utilityNameRequired'))
			return
		}
		setUtilitiesError(undefined)
		onValid({ ...values, utilities: kept })
	}

	const depositEnabled = form.watch('depositEnabled')
	const penaltyEnabled = form.watch('penaltyEnabled')
	const paymentForm    = form.watch('paymentForm')

	const utilitiesTotal = utilities.reduce((acc, u) => acc + (toRow(u).amount ?? 0), 0)

	// ── Rent VAT breakdown ─────────────────────────────────────────────────
	// Semantics of `rentVatInclusive`:
	//   • true  → `rentAmount` is gross (already includes VAT), formula `amt * r / (100 + r)`
	//   • false → `rentAmount` is net,   formula `amt * r / 100`; `gross = amt + vat`
	// Rate of 0 means "без НДС" — suppress the hint entirely.
	const rentAmount       = form.watch('rentAmount')
	const rentVatRate      = form.watch('rentVatRate')
	const rentVatInclusive = form.watch('rentVatInclusive')
	const rentVatAmount    = rentVatRate > 0
		? (rentVatInclusive
			? (rentAmount * rentVatRate) / (100 + rentVatRate)
			: (rentAmount * rentVatRate) / 100)
		: 0
	const rentGross = rentVatInclusive ? rentAmount : rentAmount + rentVatAmount

	return (
		<Form {...form}>
			<form
				id={formId}
				onSubmit={form.handleSubmit(handleSubmit)}
				className="flex flex-col gap-6"
			>
				{/* ── Размер арендной платы ──────────────────────── */}
				<section className="flex flex-col gap-3">
					<SectionHeading>
						{t('deals.documents.wizard.payments.rentTitle')}
					</SectionHeading>
					<div className="flex gap-2">
						<FormField
							control={form.control}
							name="rentAmount"
							render={({ field }) => (
								<FormItem className="flex-1">
									<FormLabel>{t('deals.documents.wizard.payments.amount')}</FormLabel>
									<FormControl>
										<Input
											type="text"
											inputMode="decimal"
											value={String(field.value)}
											onChange={(e) => field.onChange(parseNumberInput(e.target.value))}
										/>
									</FormControl>
								</FormItem>
							)}
						/>
						{/* The document restates the rental's terms, so its currency
						    follows the rental rather than being picked again here. */}
						<CurrencyField className="w-40" value={form.watch('rentCurrency')} />
					</div>
					{/* Ставка и «входит в цену» приходят из условий аренды объекта —
					    редактировать их в документе значило бы разойтись с ними. */}
					{rentVatRate > 0 && (
						<p className="text-xs leading-4 text-(--color-muted-foreground)">
							{rentVatInclusive
								? t('deals.documents.wizard.payments.rentVatHintInclusive', {
									rate:   rentVatRate,
									amount: formatCurrency(Math.round(rentVatAmount)),
								})
								: t('deals.documents.wizard.payments.rentVatHintExclusive', {
									rate:   rentVatRate,
									amount: formatCurrency(Math.round(rentVatAmount)),
									gross:  formatCurrency(Math.round(rentGross)),
								})}
						</p>
					)}
				</section>

				{/* ── Коммунальные расходы ───────────────────────── */}
				<section className="flex flex-col gap-3">
					<EditableSectionHeader
						title={t('deals.documents.wizard.payments.utilitiesTitle')}
						editable={utilitiesEditable}
						onToggle={() => setUtilitiesEditable((v) => !v)}
						editLabel={t('deals.documents.wizard.payments.editUtilities')}
					/>
					<div className="overflow-hidden rounded-(--radius-md) border border-(--color-border) bg-(--color-background)">
						<Table className="table-fixed">
							<TableHeader>
								<TableRow className="hover:bg-transparent">
									<TableHead>{t('deals.documents.wizard.payments.utilityName')}</TableHead>
									<TableHead className="w-[200px]">{t('deals.documents.wizard.payments.utilityCalcType')}</TableHead>
									<TableHead className="w-[160px] text-right">{t('deals.documents.wizard.payments.utilityAmount')}</TableHead>
									{utilitiesEditable && <TableHead className="w-[44px]" />}
								</TableRow>
							</TableHeader>
							<TableBody>
								{utilities.map((u) => (
									<TableRow key={u.id}>
										<TableCell className="px-2 py-2">
											<input
												type="text"
												value={u.name}
												onChange={(e) => updateUtilityRow(u.id, 'name', e.target.value)}
												placeholder={t('deals.documents.wizard.payments.utilityNamePlaceholder')}
												readOnly={!utilitiesEditable}
												className={cellInputClass}
											/>
										</TableCell>
										<TableCell className="px-2 py-2">
											{utilitiesEditable ? (
												<Select
													value={u.calcType}
													onValueChange={(v) => updateUtilityRow(u.id, 'calcType', v)}
												>
													<SelectTrigger size="md" className="w-full">
														<SelectValue />
													</SelectTrigger>
													<SelectContent>
														{UTILITY_CALC_KEYS.map((k) => (
															<SelectItem key={k} value={k}>
																{t(`deals.documents.wizard.payments.calc_${k}`)}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
											) : (
												<span className="px-2 text-sm text-(--color-foreground)">
													{t(`deals.documents.wizard.payments.calc_${u.calcType}`, { defaultValue: u.calcType })}
												</span>
											)}
										</TableCell>
										<TableCell className="px-2 py-2 text-right">
											{utilitiesEditable ? (
												<input
													type="text"
													inputMode="decimal"
													value={u.amountInput}
													onChange={(e) => updateUtilityRow(u.id, 'amountInput', e.target.value)}
													onBlur={() => updateUtilityRow(
														u.id, 'amountInput',
														u.amountInput.trim() === '' ? '' : String(parseNumberInput(u.amountInput)),
													)}
													placeholder={t('deals.documents.wizard.payments.amountLaterLabel')}
													className={`${cellInputClass} text-right`}
												/>
											) : (
												<span className="text-sm text-(--color-foreground)">
													{u.amountInput.trim() === ''
														? t('deals.documents.wizard.payments.amountLaterLabel')
														: formatCurrency(parseNumberInput(u.amountInput))}
												</span>
											)}
										</TableCell>
										{utilitiesEditable && (
											<TableCell className="px-2 py-2">
												<IconButton
													type="button"
													variant="ghost"
													size="sm"
													aria-label={t('deals.documents.wizard.payments.removeUtility')}
													onClick={() => removeUtilityRow(u.id)}
												>
													<Trash2Icon className="size-3.5" />
												</IconButton>
											</TableCell>
										)}
									</TableRow>
								))}
								<TableRow className="bg-(--color-muted)/30 hover:bg-(--color-muted)/30">
									<TableCell colSpan={2} className="px-4 py-3 text-sm font-[500] text-(--color-foreground)">
										{t('deals.documents.wizard.payments.utilitiesTotal')}
									</TableCell>
									<TableCell colSpan={utilitiesEditable ? 2 : 1} className="px-4 py-3 text-right text-sm font-[500] text-(--color-foreground)">
										{formatCurrency(utilitiesTotal)}
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</div>
					{utilitiesError && (
						<p className="text-sm text-(--color-destructive)">{utilitiesError}</p>
					)}
					{utilitiesEditable && (
						<Button
							type="button"
							variant="outline"
							size="full"
							iconLeft={<PlusIcon className="size-4" />}
							onClick={addUtilityRow}
						>
							{t('deals.documents.wizard.payments.addUtility')}
						</Button>
					)}
				</section>

				{/* ── Обеспечительный платеж ─────────────────────── */}
				<section className="flex flex-col gap-3">
					<div className="flex items-center gap-4">
						<SectionHeading>
							{t('deals.documents.wizard.payments.depositTitle')}
						</SectionHeading>
						<FormField
							control={form.control}
							name="depositEnabled"
							render={({ field }) => (
								<Switch
									checked={field.value}
									onCheckedChange={(c) => {
										field.onChange(c === true)
										// Выключенный залог должен и сумму обнулить: иначе в
										// данных остаётся 50 000, а в тексте договора печатается
										// «не предусмотрен» — документ спорит сам с собой.
										if (c !== true) form.setValue('depositAmount', 0)
									}}
								/>
							)}
						/>
					</div>

					{depositEnabled && (
						<>
							<div className="flex gap-2">
								<FormField
									control={form.control}
									name="depositAmount"
									render={({ field }) => (
										<FormItem className="flex-1">
											<FormLabel>{t('deals.documents.wizard.payments.amount')}</FormLabel>
											<FormControl>
												<Input
													type="text"
													inputMode="decimal"
													value={String(field.value)}
													onChange={(e) => field.onChange(parseNumberInput(e.target.value))}
												/>
											</FormControl>
										</FormItem>
									)}
								/>
								<CurrencyField className="w-40" value={form.watch('depositCurrency')} />
							</div>

							<FormItem>
								<FormLabel>{t('deals.documents.wizard.payments.depositTerm')}</FormLabel>
								<div className="flex gap-2">
									<FormField
										control={form.control}
										name="depositTermCount"
										render={({ field }) => (
											<FormControl>
												<Input
													type="number"
													inputMode="numeric"
													value={String(field.value)}
													onChange={(e) => field.onChange(parseIntSafe(e.target.value))}
													className="w-20"
												/>
											</FormControl>
										)}
									/>
									<FormField
										control={form.control}
										name="depositTermUnit"
										render={({ field }) => (
											<Select value={field.value} onValueChange={field.onChange}>
												<FormControl>
													<SelectTrigger size="md" className="flex-1">
														<SelectValue />
													</SelectTrigger>
												</FormControl>
												<SelectContent>
													{UNIT_KEYS.map((k) => (
														<SelectItem key={k} value={k}>
															{t(`deals.documents.wizard.terms.unit_${k}`)}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										)}
									/>
									<FormField
										control={form.control}
										name="depositTermTiming"
										render={({ field }) => (
											<Select value={field.value} onValueChange={field.onChange}>
												<FormControl>
													<SelectTrigger size="md" className="flex-1">
														<SelectValue placeholder={t('deals.documents.wizard.terms.timing_afterContractEnd')} />
													</SelectTrigger>
												</FormControl>
												<SelectContent>
													{TIMING_KEYS.map((k) => (
														<SelectItem key={k} value={k}>
															{t(`deals.documents.wizard.terms.timing_${k}`)}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										)}
									/>
								</div>
							</FormItem>

							<FormField
								control={form.control}
								name="depositReturn"
								render={({ field }) => (
									<FormItem>
										<FormLabel>{t('deals.documents.wizard.payments.depositReturn')}</FormLabel>
										<Select value={field.value} onValueChange={field.onChange}>
											<FormControl>
												<SelectTrigger>
													<SelectValue placeholder={t('deals.documents.wizard.payments.depositReturn_lastPeriodOffset')} />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{DEPOSIT_RETURN_KEYS.map((k) => (
													<SelectItem key={k} value={k}>
														{t(`deals.documents.wizard.payments.depositReturn_${k}`)}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="depositNoReturnOnEarly"
								render={({ field }) => (
									<Label htmlFor="deposit-no-return" className="flex items-center gap-2 cursor-pointer">
										<Checkbox
											id="deposit-no-return"
											checked={field.value}
											onCheckedChange={(c) => field.onChange(c === true)}
										/>
										<span className="text-sm leading-none font-[500] text-(--color-foreground)">
											{t('deals.documents.wizard.payments.depositNoReturnOnEarly')}
										</span>
									</Label>
								)}
							/>
						</>
					)}
				</section>

				{/* ── Пени за просрочку ──────────────────────────── */}
				<section className="flex flex-col gap-3">
					<div className="flex items-center gap-4">
						<SectionHeading>
							{t('deals.documents.wizard.payments.penaltyTitle')}
						</SectionHeading>
						<FormField
							control={form.control}
							name="penaltyEnabled"
							render={({ field }) => (
								<Switch
									checked={field.value}
									onCheckedChange={(c) => {
										field.onChange(c === true)
										if (c !== true) form.setValue('penaltyPercent', 0)
									}}
								/>
							)}
						/>
					</div>

					{penaltyEnabled && (
						<div className="flex gap-2">
							<FormField
								control={form.control}
								name="penaltyPercent"
								render={({ field }) => (
									<FormItem className="flex-1">
										<FormLabel>{t('deals.documents.wizard.payments.penaltyPercent')}</FormLabel>
										<FormControl>
											<Input
												inputMode="decimal"
												value={String(field.value)}
												onChange={(e) => field.onChange(parseNumberInput(e.target.value))}
												suffix="%"
											/>
										</FormControl>
									</FormItem>
								)}
							/>
							<FormItem className="flex-1">
								<FormLabel>{t('deals.documents.wizard.payments.penaltyGrace')}</FormLabel>
								<div className="flex gap-2">
									<FormField
										control={form.control}
										name="penaltyGraceCount"
										render={({ field }) => (
											<FormControl>
												<Input
													type="number"
													inputMode="numeric"
													value={String(field.value)}
													onChange={(e) => field.onChange(parseIntSafe(e.target.value))}
													className="w-20"
												/>
											</FormControl>
										)}
									/>
									<FormField
										control={form.control}
										name="penaltyGraceUnit"
										render={({ field }) => (
											<Select value={field.value} onValueChange={field.onChange}>
												<FormControl>
													<SelectTrigger size="md" className="flex-1">
														<SelectValue />
													</SelectTrigger>
												</FormControl>
												<SelectContent>
													{UNIT_KEYS.map((k) => (
														<SelectItem key={k} value={k}>
															{t(`deals.documents.wizard.terms.unit_${k}`)}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										)}
									/>
								</div>
							</FormItem>
						</div>
					)}
				</section>

				{/* ── Срок внесения первого платежа ──────────────── */}
				<section className="flex flex-col gap-3">
					<SectionHeading>
						{t('deals.documents.wizard.payments.firstPaymentTitle')}
					</SectionHeading>
					<FormField
						control={form.control}
						name="firstPaymentDays"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('deals.documents.wizard.payments.firstPaymentHint')}</FormLabel>
								<FormControl>
									<Input
										type="number"
										inputMode="numeric"
										value={String(field.value)}
										onChange={(e) => field.onChange(parseIntSafe(e.target.value))}
										suffix={t('deals.documents.wizard.terms.unit_days')}
									/>
								</FormControl>
							</FormItem>
						)}
					/>
				</section>

				{/* ── Порядок перечисления арендной платы ────────── */}
				<section className="flex flex-col gap-3">
					<SectionHeading>
						{t('deals.documents.wizard.payments.transferOrderTitle')}
					</SectionHeading>
					<div className="flex gap-2">
						<FormField
							control={form.control}
							name="paymentForm"
							render={({ field }) => (
								<FormItem className="flex-1">
									<FormLabel>{t('deals.documents.wizard.payments.paymentForm')}</FormLabel>
									<Select value={field.value} onValueChange={field.onChange}>
										<FormControl>
											<SelectTrigger size="md" className="w-full">
												<SelectValue />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											{PAYMENT_FORM_KEYS.map((k) => (
												<SelectItem key={k} value={k}>
													{t(`deals.documents.wizard.payments.paymentForm_${k}`)}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="landlordAccountId"
							render={({ field }) => (
								<FormItem className="flex-1">
									<FormLabel>{t('deals.documents.wizard.payments.landlordAccount')}</FormLabel>
									<FormControl>
										<Input
											{...field}
											placeholder="123"
											disabled={paymentForm !== 'cashless'}
										/>
									</FormControl>
								</FormItem>
							)}
						/>
					</div>
				</section>

				{/* ── Условия оплаты ─────────────────────────────── */}
				<section className="flex flex-col gap-3">
					<SectionHeading>
						{t('deals.documents.wizard.payments.termsTitle')}
					</SectionHeading>
					<FormField
						control={form.control}
						name="periodStart"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('deals.documents.wizard.payments.periodStart')}</FormLabel>
								<RadioGroup
									value={field.value}
									onValueChange={(v) => field.onChange(v as PaymentPeriodStart)}
									className="flex flex-wrap gap-4"
								>
									{PAYMENT_PERIOD_STARTS.map((k) => (
										<RadioOption
											key={k}
											value={k}
											id={`period-start-${k}`}
											bordered={false}
											label={t(`deals.documents.wizard.payments.periodStart_${k}`)}
										/>
									))}
								</RadioGroup>
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="paymentDay"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('deals.documents.wizard.payments.paymentDay')}</FormLabel>
								<Select value={field.value} onValueChange={field.onChange}>
									<FormControl>
										<SelectTrigger>
											<SelectValue placeholder={t('deals.documents.wizard.payments.paymentDay_every5th')} />
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										{PAYMENT_DAY_KEYS.map((k) => (
											<SelectItem key={k} value={k}>
												{t(`deals.documents.wizard.payments.paymentDay_${k}`)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="paymentPriority"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('deals.documents.wizard.payments.paymentPriority')}</FormLabel>
								<Select value={field.value} onValueChange={field.onChange}>
									<FormControl>
										<SelectTrigger>
											<SelectValue placeholder={t('deals.documents.wizard.payments.priority_penaltyOverdueCurrent')} />
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										{PRIORITY_KEYS.map((k) => (
											<SelectItem key={k} value={k}>
												{t(`deals.documents.wizard.payments.priority_${k}`)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</FormItem>
						)}
					/>
				</section>

				{/* ── Пример графика платежей ────────────────────── */}
				<section className="flex flex-col gap-3">
					<SectionHeading>
						{t('deals.documents.wizard.payments.scheduleTitle')}
					</SectionHeading>
					<div className="overflow-hidden rounded-(--radius-md) border border-(--color-border) bg-(--color-background)">
						<Table className="table-fixed">
							<TableHeader>
								<TableRow className="hover:bg-transparent">
									<TableHead className="w-[52px]">{t('deals.documents.wizard.payments.scheduleIdx')}</TableHead>
									<TableHead>{t('deals.documents.wizard.payments.schedulePeriod')}</TableHead>
									<TableHead className="w-[160px] text-right">{t('deals.documents.wizard.payments.scheduleDueDate')}</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{SCHEDULE_PREVIEW_ROWS.map((r) => (
									<TableRow key={r.idx}>
										<TableCell className="px-4 py-3 text-sm text-(--color-foreground)">{r.idx}</TableCell>
										<TableCell className="px-4 py-3 text-sm text-(--color-foreground)">{r.period}</TableCell>
										<TableCell className="px-4 py-3 text-right text-sm text-(--color-foreground)">{r.dueDate}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				</section>
			</form>
		</Form>
	)
}
